import { CertificateDocumentStatus } from '../../../../../../../enums/CertificateDocumentStatus'
import { CertificateDocumentTypeV2 } from '../../../../../../../enums/CertificateDocumentType'
import { CategoryType } from '../../../../../../../enums/CetificateFileCategoryType'
import { CertificateDetailsModel } from '../../../../../../../shared/models/CertificateDetailsModel'
import { CertificateFileResultModel } from '../../../../../../../shared/models/CertificateFileResultModel'
import {
  CertificateGroupEnum,
  getAttributeUniqueName,
} from '../../../../../../../utils/constants/certificate.constants'
import { IEcmCategoryModel } from '../interfaces/ecm-category-model/IECMCategoryModel'
import { IAddAttachmentsRPDropdownOption } from '../interfaces/IAddAttachmentsRPDropdownOption'
import { IAddAttachmentsRPTestReportModel } from '../interfaces/IAddAttachmentsRPTestReportModel'
import { IPaginatedRPDocumentAttachments } from '../interfaces/IAddAttachmentsRPTestReportModel'
import { v4 as uuid } from 'uuid'
import { IAddAttachmentsRPTestSupportingDocumentModel } from '../interfaces/IAddAttachmentsRPTestSupportingDocumentModel'
import { ICertificateRPDocumentsResultModel } from '../interfaces/ICertificateRPDocumentsResultModel'
import { IPaginatedCertificateRPDocumentsResultModel } from '../interfaces/ICertificateRPDocumentsResultModel'
import {
  LinkedToWbAndEcmTicText,
  ReadyToLinkToWbAndEcmTicText,
  ReadyToReLinkToWbAndEcmTicText,
  SavedInEcmTicText,
  TestReportDuplicateLinkToEcmFailedHover,
  UnlinkingToEcmInProgressText,
  UploadToBlobFailedText,
  UploadToEcmInProgressText,
} from '../constants/AddAttachmentsRPConstants'
import { CertificateRPFileRequestModel } from '../../../../../../../shared/models/CertificateRPFileRequestModel'
import { LinkWbAndEcmRequestModel } from '../../../../../../../shared/models/LinkWbAndEcmRequestModel'
import { IConfirmationModalProps } from '../interfaces/non-model/IConfirmationModalProps'
import moment from 'moment'

const cgFirstLevelOptions = process.env.REACT_APP_CM_CONTENT_GROUP_FIRST_LEVEL_OPTIONS
const cgSecondLevelOptions = process.env.REACT_APP_CM_CONTENT_GROUP_SECOND_LEVEL_OPTIONS

const originalLevelOptionValue = 'original'
const trCertificateLevelOptionValue = 'trcertificate'

const allowedToLinkContentGroup =
  process.env.REACT_APP_RP_ALLOWED_LINK_CONTENT_GROUPS?.split(',') ?? []

const allowedToLinkScheme = process.env.REACT_APP_RP_ALLOWED_LINK_SCHEMES?.split(',') ?? []
const allowedToLinkAttachmentType =
  process.env.REACT_APP_RP_ALLOWED_LINK_ATTACHMENT_TYPES?.split(',') ?? []

const workbenchDocumentLinkUrl = process.env.REACT_APP_WORKBENCH_DOCUMENT_URL

const excludedPropertiesInComparison = ['status']

export const AddAttachmentsRPUtils = {
  filterCgFirstLevelOptions: (rawOptions: IAddAttachmentsRPDropdownOption[]) =>
    rawOptions.filter(rawOption =>
      cgFirstLevelOptions
        ?.split(',')
        .map(firstLevel => firstLevel.toLowerCase())
        .includes(rawOption.label.toLowerCase())
    ),

  filterCgSecondLevelOptions: (rawOptions: IAddAttachmentsRPDropdownOption[]) =>
    rawOptions.filter(rawOption =>
      cgSecondLevelOptions
        ?.split(',')
        .map(secondLevel => secondLevel.toLowerCase())
        .includes(rawOption.label.toLowerCase())
    ),

  transformDropdownOptions: (
    ecmCategoryModelList: IEcmCategoryModel[],
    keyName: string,
    includeNoneOption: boolean = false
  ): IAddAttachmentsRPDropdownOption[] => {
    const dropdownOptions: IAddAttachmentsRPDropdownOption[] = []
    const ecmOptions = ecmCategoryModelList
      ?.map(res =>
        res.attributes?.filter(attr => attr.name === keyName && attr.validValues !== undefined)
      )
      ?.map(ecm => ecm?.map(values => values.validValues))[0]

    if (ecmOptions !== undefined) {
      ecmOptions.forEach((ecm, index) => {
        ecm?.forEach((ecmGroupValues, index) => {
          if (ecmGroupValues) {
            const newOption: IAddAttachmentsRPDropdownOption = {
              label: ecmGroupValues,
              value: ecmGroupValues,
            }
            dropdownOptions.push(newOption)
          }
        })
      })
    }

    if (includeNoneOption === true) {
      const noneDropdownOption: IAddAttachmentsRPDropdownOption = {
        label: 'None',
        value: '',
      }

      dropdownOptions.unshift(noneDropdownOption)
    }

    return dropdownOptions
  },

  isContentGroupOriginal: (contentGroup: string) => {
    return contentGroup.toLowerCase() === originalLevelOptionValue
  },
  isContentGroupTRCertificate: (contentGroup: string) => {
    return contentGroup.toLowerCase() === trCertificateLevelOptionValue
  },
  isReportNumberRequired: (contentGroup: string) => {
    if (!contentGroup) return

    const contentGroupsToCheck = ['Amendment', 'Correction', 'Reissue']

    return contentGroupsToCheck.some(cg => cg.toLowerCase() === contentGroup.toLowerCase())
  },
  isDisableInternalAttachmentType: (contentGroup: string) => {
    if (!contentGroup) return

    const contentGroupsToCheck = ['Test Report', 'TRAttachment', 'TRCertificate', 'TRCoverPage']

    return contentGroupsToCheck.some(cg => cg.toLowerCase() === contentGroup.toLowerCase())
  },
  isDisableAttachmentType: (contentGroup: string) => {
    if (!contentGroup) return

    const contentGroupsToCheck = ['ULInternalAttachment', 'ULInternalDataSheet', 'ULInternalSource']

    return contentGroupsToCheck.some(cg => cg.toLowerCase() === contentGroup.toLowerCase())
  },
  formatCertificateNumber: (certificateNumber: string, revisionNumber: string) => {
    return `${certificateNumber}-${revisionNumber}`
  },
  getAttributeValueNullableCheck: (
    formValues: any,
    certificateGroup: CertificateGroupEnum,
    field: string
  ) => {
    if (!formValues) return ''

    return formValues[getAttributeUniqueName(certificateGroup, field)] ?? ''
  },
  convertRPModelToCreateCertificateModel: (
    source: IAddAttachmentsRPTestReportModel[],
    writebackData: any,
    isSaveAndActivate: boolean
  ): CertificateDetailsModel => {
    const createCertificateModel: CertificateDetailsModel = {
      certificateDetails: writebackData.certificateDetails,
      certificateFiles: source.flatMap(rpTestReport => {
        let certificateFilesReturn: CertificateFileResultModel[] = []
        const parentId = rpTestReport.status ? rpTestReport.id : uuid()
        //Level 1
        certificateFilesReturn.push({
          id: rpTestReport.status ? rpTestReport.id : null,
          file: rpTestReport.file,
          categoryType: CategoryType.None,
          documentType: CertificateDocumentTypeV2.None,
          contentType: null,
          ecmNodeId: rpTestReport?.ecmFileNodeId ?? '',
          filename: rpTestReport.documentFileName,
          originalFileName: rpTestReport.documentFileName,
          isDeleted: false,
          status: rpTestReport?.status
            ? rpTestReport.status
            : CertificateDocumentStatus.ReadyToLinkToWbAndEcmTic, //TODO: handle Status

          //RP Values
          contentGroup: rpTestReport.contentGroup,
          type: rpTestReport.type,
          referenceNumber: rpTestReport.referenceNumber,
          projectNumber: rpTestReport.projectNumber,
          standardNumber: rpTestReport.standardNumber,
          issueDate: rpTestReport.issueDate ?? '',
          fileNumber: rpTestReport.fileNumber ?? '',
          revisionNumber: rpTestReport.revisionNumber,
          reportNumber: rpTestReport.reportNumber,
          attachmentType: null,
          internalAttachmentType: null,
          certificateNumber: null,
          certificateDocumentId: parentId,
          parentCertificateDocumentId: null,
          isToLinkTestReport: rpTestReport.isToLinkTestReport,
        })

        rpTestReport.supportingDocuments.forEach(
          (suppDoc: IAddAttachmentsRPTestSupportingDocumentModel) => {
            //Level 2
            certificateFilesReturn.push({
              id: suppDoc.status ? suppDoc.id : null,
              file: suppDoc.file,
              categoryType: CategoryType.None,
              documentType: CertificateDocumentTypeV2.None,
              contentType: null,
              ecmNodeId: suppDoc?.ecmFileNodeId ?? '',
              filename: suppDoc.documentFileName,
              originalFileName: suppDoc.documentFileName,
              isDeleted: false,
              status: rpTestReport?.ecmFileNodeId
                ? rpTestReport.status!
                : CertificateDocumentStatus.ReadyToLinkToWbAndEcmTic,
              //RP Values
              contentGroup: suppDoc.contentGroup,
              type: suppDoc.type,
              referenceNumber: '',
              projectNumber: suppDoc.projectNumber,
              standardNumber: AddAttachmentsRPUtils.isContentGroupTRCertificate(
                suppDoc.contentGroup
              )
                ? suppDoc.standardNumber
                : null,
              issueDate: AddAttachmentsRPUtils.isContentGroupTRCertificate(suppDoc.contentGroup)
                ? suppDoc.issueDate
                : null,
              fileNumber: suppDoc.fileNumber ?? '',
              revisionNumber: suppDoc.revisionNumber,
              reportNumber: '',
              attachmentType: suppDoc.attachmentType,
              internalAttachmentType: suppDoc.internalAttachmentType,
              certificateNumber: suppDoc.certificateNumber,
              certificateDocumentId: uuid(),
              parentCertificateDocumentId: parentId,
              isToLinkTestReport: false,
            })
          }
        )

        return certificateFilesReturn
      }),
      isSaveAndActivate: isSaveAndActivate,
    }

    return createCertificateModel
  },
  convertRPModelToAttachmentFileRequestModel: (
    certificateId: string,
    source: IAddAttachmentsRPTestReportModel[],
    IPCertificateDetailsModel: any,
    isSaveAndActivate: boolean
  ): LinkWbAndEcmRequestModel => {
    const linkWbAndEcmRequestModel: LinkWbAndEcmRequestModel = {
      certificateId: certificateId,
      certificateFiles: source.flatMap(rpTestReport => {
        let certificateFilesReturn: CertificateRPFileRequestModel[] = []
        const parentId = rpTestReport.status ? rpTestReport.id : uuid()
        //Level 1
        certificateFilesReturn.push({
          id: rpTestReport.status ? rpTestReport.id : null,
          file: rpTestReport.file,
          categoryType: CategoryType.None,
          documentType: CertificateDocumentTypeV2.None,
          contentType: null,
          ecmNodeId: rpTestReport?.ecmFileNodeId ?? '',
          filename: rpTestReport.documentFileName,
          isDeleted: false,
          status: rpTestReport?.status
            ? rpTestReport.status
            : CertificateDocumentStatus.ReadyToLinkToWbAndEcmTic,

          //RP Values
          contentGroup: rpTestReport.contentGroup,
          type: rpTestReport.type,
          referenceNumber: rpTestReport.referenceNumber,
          projectNumber: rpTestReport?.projectNumber ?? rpTestReport?.serviceRequestNumber,
          standardNumber: rpTestReport.standardNumber,
          issueDate: rpTestReport.issueDate,
          fileNumber: rpTestReport.fileNumber ?? '',
          revisionNumber: rpTestReport.revisionNumber,
          reportNumber: rpTestReport.reportNumber,
          attachmentType: null,
          internalAttachmentType: null,
          certificateNumber: null,
          certificateDocumentId: parentId,
          parentCertificateDocumentId: null,
          isToLinkTestReport: rpTestReport.isToLinkTestReport,
        })

        rpTestReport.supportingDocuments.forEach(
          (suppDoc: IAddAttachmentsRPTestSupportingDocumentModel) => {
            //Level 2
            certificateFilesReturn.push({
              id: suppDoc.status ? suppDoc.id : null,
              file: suppDoc.file,
              categoryType: CategoryType.None,
              documentType: CertificateDocumentTypeV2.None,
              contentType: null,
              ecmNodeId: suppDoc?.ecmFileNodeId ?? '',
              filename: suppDoc.documentFileName,
              isDeleted: false,
              status: rpTestReport?.ecmFileNodeId
                ? rpTestReport.status!
                : CertificateDocumentStatus.ReadyToLinkToWbAndEcmTic,
              //RP Values
              contentGroup: suppDoc.contentGroup,
              type: suppDoc.type,
              referenceNumber: '',
              projectNumber: suppDoc?.projectNumber ?? suppDoc?.serviceRequestNumber,
              standardNumber: AddAttachmentsRPUtils.isContentGroupTRCertificate(
                suppDoc.contentGroup
              )
                ? suppDoc.standardNumber
                : null,
              issueDate: AddAttachmentsRPUtils.isContentGroupTRCertificate(suppDoc.contentGroup)
                ? suppDoc.issueDate
                : null,
              fileNumber: suppDoc.fileNumber ?? '',
              revisionNumber: suppDoc.revisionNumber,
              reportNumber: '',
              attachmentType: suppDoc.attachmentType,
              internalAttachmentType: suppDoc.internalAttachmentType,
              certificateNumber: suppDoc.certificateNumber,
              certificateDocumentId: uuid(),
              parentCertificateDocumentId: parentId,
              isToLinkTestReport: false,
            })
          }
        )

        return certificateFilesReturn
      }),
      isSaveAndActivate: isSaveAndActivate,
      ipCertificateDetailsModelJson: JSON.stringify(IPCertificateDetailsModel),
    }
    return linkWbAndEcmRequestModel
  },
  mapCertificateAttachmentsToRPModel: (
    rpAttachmentsFromApi: ICertificateRPDocumentsResultModel[]
  ): IAddAttachmentsRPTestReportModel[] => {
    let attachmentsRPModel: IAddAttachmentsRPTestReportModel[] = []

    rpAttachmentsFromApi
      ?.filter(rpAttachmentsFromApi => !rpAttachmentsFromApi.parentCertificateDocumentId)
      .forEach(rpAttachmentFromApi => {
        const rpModel: IAddAttachmentsRPTestReportModel = {
          id: rpAttachmentFromApi.id,
          documentFileName: rpAttachmentFromApi.originalFileName ?? '',
          contentGroup: rpAttachmentFromApi.contentGroup ?? '',
          type: rpAttachmentFromApi.type ?? '',
          ecmFileNodeId: rpAttachmentFromApi.ecmFileNodeId ?? '',
          fileNumber: rpAttachmentFromApi.fileNumber ?? '',
          issueDate: rpAttachmentFromApi?.issueDate,
          projectNumber: rpAttachmentFromApi.projectNumber ?? '',
          reportNumber: rpAttachmentFromApi.reportNumber ?? '',
          standardNumber: rpAttachmentFromApi.standardNumber ?? '',
          revisionNumber: rpAttachmentFromApi.revisionNumber ?? '',
          isValid: true, // TODO: Handle Is Valid,
          fileResult: '',
          referenceNumber: rpAttachmentFromApi.referenceNumber ?? '',
          status: rpAttachmentFromApi.status,
          file: null,
          ecmFileName: rpAttachmentFromApi.ecmFileName,
          isToLinkTestReport: false,
          supportingDocuments: rpAttachmentsFromApi
            .filter(child => child.parentCertificateDocumentId === rpAttachmentFromApi.id)
            .map(childAttachment => {
              const suppDocModel: IAddAttachmentsRPTestSupportingDocumentModel = {
                id: childAttachment.id,
                documentFileName: childAttachment.originalFileName ?? '',
                contentGroup: childAttachment.contentGroup ?? '',
                type: childAttachment.type ?? '',
                ecmFileNodeId: childAttachment.ecmFileNodeId ?? '',
                fileNumber: childAttachment.fileNumber ?? '',
                issueDate: childAttachment?.issueDate
                  ? moment(childAttachment?.issueDate).format('YYYY-MM-DD')
                  : null,
                projectNumber: childAttachment.projectNumber ?? '',
                certificateNumber: childAttachment.certificateNumber ?? '',
                standardNumber: childAttachment.standardNumber ?? '',
                revisionNumber: childAttachment.revisionNumber ?? '',
                isValid: true, // TODO: Handle Is Valid,
                fileResult: '',
                attachmentType: childAttachment.attachmentType ?? '',
                internalAttachmentType: childAttachment.internalAttachmentType ?? '',
                file: null,
                status: childAttachment.status,
                ecmFileName: childAttachment.ecmFileName,
              }
              return suppDocModel
            }),
        }
        attachmentsRPModel.push(rpModel)
      })

    return attachmentsRPModel
  },

  mapPaginatedCertificateAttachmentsToRPModel: (
    paginatedRPAttachmentsFromApi: IPaginatedCertificateRPDocumentsResultModel | null
  ): IPaginatedRPDocumentAttachments => {
    return {
      records: AddAttachmentsRPUtils.mapCertificateAttachmentsToRPModel(
        paginatedRPAttachmentsFromApi?.records ?? []
      ),
      total: paginatedRPAttachmentsFromApi?.total ?? 0,
      pageNumber: paginatedRPAttachmentsFromApi?.pageNumber ?? 0,
      totalPages: paginatedRPAttachmentsFromApi?.totalPages ?? 0,
      hasNextPage: paginatedRPAttachmentsFromApi?.hasNextPage ?? false,
    }
  },

  getDocumentStatusText: (status: CertificateDocumentStatus | null) => {
    switch (status) {
      case CertificateDocumentStatus.UploadToEcmInProgress:
      case CertificateDocumentStatus.NotYetUploaded:
        return UploadToEcmInProgressText
      case CertificateDocumentStatus.UploadToBlobFailed:
        return UploadToBlobFailedText
      case CertificateDocumentStatus.LinkedToWbAndEcmTic:
        return LinkedToWbAndEcmTicText
      case CertificateDocumentStatus.SavedInEcmTic:
        return SavedInEcmTicText
      case CertificateDocumentStatus.ReadyToLinkToWbAndEcmTic:
        return ReadyToLinkToWbAndEcmTicText
      case CertificateDocumentStatus.UploadToEcmFailed:
        return ReadyToReLinkToWbAndEcmTicText
      case CertificateDocumentStatus.TestReportDuplicate:
        return ReadyToLinkToWbAndEcmTicText
      case CertificateDocumentStatus.TestReportDuplicateLinkToEcmFailed:
        return TestReportDuplicateLinkToEcmFailedHover
      case CertificateDocumentStatus.UnlinkingInProgress:
        return UnlinkingToEcmInProgressText
    }
  },

  isStatusUploading: (status: CertificateDocumentStatus | null): boolean => {
    return (
      status === CertificateDocumentStatus.UploadToEcmInProgress ||
      status === CertificateDocumentStatus.NotYetUploaded ||
      status === CertificateDocumentStatus.TestReportDuplicateLinkToEcmInProgress ||
      status === CertificateDocumentStatus.RetryUploadToEcmTicInProgress
    )
  },
  isFieldReadonly: (ecmFileNodeId: string | undefined, hasRevisions: boolean = false): boolean => {
    if (!ecmFileNodeId) return false

    return ecmFileNodeId.length > 0
  },

  initialConfirmationModalValues: (): IConfirmationModalProps => {
    const initialValues: IConfirmationModalProps = {
      rpAttachmentData: null,
      onConfirm: () => {},
      onCancel: () => {},
      visible: false,
      headerDisplay: '',
      buttonNoDisplay: 'No',
      buttonYesDisplay: 'Yes',
      confirmationMessageHTML: '',
      style: undefined,
      isSaving: undefined,
    }

    return initialValues
  },
  shouldShowLinkButtonFlyout: (
    contentGroup: string,
    status: CertificateDocumentStatus,
    scheme: string,
    attachmentType?: string
  ): boolean => {
    return (
      AddAttachmentsRPUtils.isDocumentAllowedToLink(scheme, contentGroup, attachmentType) ||
      status === CertificateDocumentStatus.LinkedToWbAndEcmTic ||
      status === CertificateDocumentStatus.UploadToEcmFailed
    )
  },
  getWorkbenchDocumentLinkUrl: (): string | undefined => {
    return workbenchDocumentLinkUrl
  },

  mapInitialIssueDateValue: (
    rpAttachments: IAddAttachmentsRPTestReportModel[],
    testReportNumbers: any,
    testReportIssueDates: any
  ) => {
    rpAttachments.forEach(attachment => {
      if (!attachment?.ecmFileNodeId) {
        const fileNameIndex = attachment.documentFileName.lastIndexOf('.')
        const fileName = attachment.documentFileName.slice(0, fileNameIndex)
        const reportNoIndex = testReportNumbers.findIndex(
          (report: any) => report.toLocaleLowerCase() === fileName.toLocaleLowerCase()
        )

        if (reportNoIndex >= 0) attachment.issueDate = testReportIssueDates[reportNoIndex]
      } else if (attachment?.ecmFileNodeId == '' && attachment?.issueDate == undefined)
        attachment.issueDate = null
    })
  },
  supportingDocumentAllowedFileTypes: [
    'PDF',
    'JPG',
    'JPEG',
    'DOC',
    'DOCX',
    'BMP',
    'GIF',
    'PNG',
    'XLS',
    'XLSX',
    'TXT',
    'MSG',
    'ZIP',
  ],
  hasNewlyAddedFiles: (rpAttachments: IAddAttachmentsRPTestReportModel[]): boolean => {
    return rpAttachments.some(
      attachment =>
        (attachment?.file && !attachment.ecmFileNodeId) ||
        attachment?.supportingDocuments.some(suppDoc => suppDoc?.file && !suppDoc.ecmFileNodeId)
    )
  },
  isLinkUnlinkInProgress: (status: CertificateDocumentStatus) => {
    return (
      status === CertificateDocumentStatus.UploadToEcmInProgress ||
      status === CertificateDocumentStatus.UnlinkingInProgress
    )
  },

  isAttachmentValidToDelete: (status: CertificateDocumentStatus) => {
    return (
      status === CertificateDocumentStatus.UploadToEcmFailed ||
      status === CertificateDocumentStatus.TestReportDuplicate ||
      status === CertificateDocumentStatus.ReadyToLinkToWbAndEcmTic
    )
  },

  isDocumentAllowedToLink: (
    scheme: string,
    contentGroup: string,
    attachmentType?: string
  ): boolean => {
    const isAllowed = allowedToLinkContentGroup
      .map(listItem => listItem.toLowerCase())
      .includes(contentGroup.toLowerCase())

    if (!isAllowed) return false

    if (contentGroup.toLowerCase() !== 'trattachment') return true

    const isAllowedSchemes = allowedToLinkScheme
      .map(listItem => listItem.toLowerCase())
      .includes(scheme.toLowerCase())

    if (!isAllowedSchemes) return false

    const isAllowedAttachmentType = allowedToLinkAttachmentType
      .map(listItem => listItem.toLowerCase())
      .includes(attachmentType?.toLowerCase() ?? '')

    return isAllowedAttachmentType
  },

  removeExcludedProperties: (value: any): any => {
    if (Array.isArray(value)) {
      return value.map(AddAttachmentsRPUtils.removeExcludedProperties)
    }

    if (value && typeof value === 'object') {
      return Object.keys(value).reduce((result: any, key: string) => {
        if (!excludedPropertiesInComparison.includes(key)) {
          result[key] = AddAttachmentsRPUtils.removeExcludedProperties(value[key])
        }

        return result
      }, {})
    }

    return value
  },
}
